import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Minus, Truck, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/common/SEO';
import { Button } from '../components/ui/button';

const Cell = ({ v }) => {
  if (v === true) return <Check className="w-4 h-4 text-minted-600 mx-auto" />;
  if (v === false) return <X className="w-4 h-4 text-red-400 mx-auto" />;
  if (v === null) return <Minus className="w-4 h-4 text-stone-300 mx-auto" />;
  return <span className="text-xs text-stone-600">{v}</span>;
};

const ROWS = [
  { label: 'Choose your own shipping method', minted: true, depop: 'New listings must use Depop Shipping', ebay: 'Labels via eBay or pickup only', vinted: 'Platform shipping' },
  { label: 'Local pickup allowed', minted: true, depop: null, ebay: true, vinted: false },
  { label: 'Buyer service fee on items', minted: 'None', depop: 'Up to 5% + A$1', ebay: 'None', vinted: 'Buyer protection fee per order' },
  { label: 'Tracked label option', minted: true, depop: true, ebay: true, vinted: true },
  { label: 'List in your own currency (A$, £, €, US$)', minted: true, depop: false, ebay: false, vinted: false },
  { label: 'Boost listings only when you choose', minted: true, depop: true, ebay: true, vinted: true },
];

export default function WhyMintedPage() {
  const navigate = useNavigate();
  return (
    <Layout title="Why Minted" showBack showSearch={false}>
      <SEO
        title="Why sell on Minted? — compare fees & shipping freedom"
        description="Compare Minted with Depop, eBay and Vinted: shipping freedom, buyer fees and seller experience. Ship your way — tracked labels, your own courier, or local pickup."
      />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-display text-2xl text-stone-900 mb-2" data-testid="why-minted-heading">
          Ship your way. Sell your way.
        </h1>
        <p className="text-sm text-stone-600 mb-6">
          Other marketplaces are locking sellers into their shipping systems. Minted doesn't — use tracked labels, your own courier, or meet locally. Here's how we compare.
        </p>

        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden mb-4">
          <table className="w-full text-left" data-testid="comparison-table">
            <thead>
              <tr className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-500">
                <th className="p-3"></th>
                <th className="p-3 text-center text-minted-700 font-semibold">Minted</th>
                <th className="p-3 text-center">Depop</th>
                <th className="p-3 text-center">eBay</th>
                <th className="p-3 text-center">Vinted</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.label} className="border-t border-stone-100 align-top">
                  <td className="p-3 text-xs font-medium text-stone-800 w-[34%]">{r.label}</td>
                  <td className="p-3 text-center bg-minted-50/40"><Cell v={r.minted} /></td>
                  <td className="p-3 text-center"><Cell v={r.depop} /></td>
                  <td className="p-3 text-center"><Cell v={r.ebay} /></td>
                  <td className="p-3 text-center"><Cell v={r.vinted} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-stone-400 mb-8">
          Comparison reflects publicly stated policies as at November 2026 (Depop Australian shipping policy effective 26 Oct 2026; eBay AU casual seller program; Vinted AU fees). Policies may change — always check each platform's current terms.
        </p>

        <div className="bg-[#1a3a2a] rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-minted-300" />
            <h2 className="font-display text-lg">Shipping freedom, built in</h2>
          </div>
          <p className="text-sm text-minted-200 mb-4">
            Tracked Shippit labels when you want convenience. Your own courier when you've got a better deal. Local pickup when it's just around the corner. Your listing, your choice.
          </p>
          <Button
            onClick={() => navigate('/sell')}
            className="bg-white text-[#1a3a2a] hover:bg-minted-50"
            data-testid="why-minted-sell-cta"
          >
            Start selling <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </Layout>
  );
}
